/**
 * useConsentRequests – React Hook (REQ-008 / Issue #111)
 * Listens to SSE stream for incoming consent requests and manages state
 */
import { useCallback, useEffect, useState } from "react";

import apiClient from "@/services/api/apiClient";
import { subscribeToEventStream } from "@/lib/sharedEventStream";
import { useAuthStore } from "../stores/authStore";

// Keep host part only; endpoints below already include /api/... paths.
const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/api\/?$/, "");

export interface ConsentRequest {
  id: string;
  patientUserId: string;
  requesterId: string;
  requesterName: string;
  requesterRole: "MEDICAL" | "OPERATOR";
  requesterInstitution?: string;
  accessLevel: "EMERGENCY" | "MEDICAL" | "OPERATOR";
  status: "PENDING" | "GRANTED" | "DENIED" | "EXPIRED";
  requestedAt: string;
  expiresAt: string;
}

export function useConsentRequests() {
  // Whether someone is signed in, not which token they hold. The token string
  // changes at sign-in and on every refresh - every five minutes - and keying
  // the effects on it tore the shared event stream down and reopened it each
  // time. Safari reports every such abort as "Fetch API cannot load ... due to
  // access control checks". The stream reads the current token itself when it
  // connects, and repairs a 401 through the refresh path.
  const signedIn = useAuthStore((state) => Boolean(state.token));
  const [pendingRequests, setPendingRequests] = useState<ConsentRequest[]>([]);
  const [responding, setResponding] = useState<string | null>(null);

  // Load existing pending requests on mount
  useEffect(() => {
    if (!signedIn) return;
    // Through apiClient, not a bare fetch: an access token lives five minutes,
    // and a bare fetch has no way to renew it. A page left open longer than
    // that answered 401 here and swallowed it, so a patient with the app in
    // front of them saw no pending request at all until they reloaded.
    apiClient
      .get<{ data: ConsentRequest[] }>("/consent-requests?status=PENDING")
      .then((response) => {
        if (response.data?.data) setPendingRequests(response.data.data);
      })
      .catch(() => undefined);
  }, [signedIn]);

  // Open the event stream
  useEffect(() => {
    if (!signedIn) return;

    // The token goes in a header, not the query string. EventSource cannot do
    // that, which is why this stream was answered 401 for as long as it
    // existed - and why the token used to end up in the proxy's access log.
    // Shared: four components mount this hook, and they need one connection
    // between them rather than four.
    return subscribeToEventStream(
      `${API_BASE}/api/events/stream`,
      (type, data) => {
        if (type === "CONSENT_REQUEST") {
          const req = JSON.parse(data) as ConsentRequest;
          setPendingRequests((prev) =>
            prev.some((r) => r.id === req.id) ? prev : [req, ...prev],
          );
          return;
        }

        if (type === "CONSENT_RESPONSE") {
          const { requestId } = JSON.parse(data) as { requestId: string };
          setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));
        }
      },
    );
  }, [signedIn]);

  const respond = useCallback(
    async (requestId: string, decision: "GRANT" | "DENY") => {
      if (!signedIn) return;
      setResponding(requestId);
      try {
        await apiClient.post(`/consent-requests/${requestId}/respond`, {
          decision,
        });
        setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));
      } finally {
        setResponding(null);
      }
    },
    [signedIn],
  );

  return {
    pendingRequests,
    pendingCount: pendingRequests.length,
    respond,
    responding,
  };
}
